import { useQuery } from '@tanstack/vue-query'
import { type Ref, computed } from 'vue'

import { useI18nAddon } from '@/i18n/i18n'
import { useCategorySeoInfo } from '@/pages/Category/use-category-seo-info'
import { usePersisterFn } from '@/query/persister-plugin'
import { Persisters } from '@/query/shared'
import { useAppStore } from '@/store/app-store'
import { type APICollectionGame, type CategoryType, PLATFORM_TYPES, type PlatformType, type Rating } from '@/types'
import { useAPI } from '@/utils/requests/api-plugin'

type CategoryLink = { hru: string; title: string }

export type CollectionGame = {
    hru: string
    title: string
    icon: string
    preview: string
    description: string
    rating: Rating
    platforms: PlatformType[]
    updatedAt: string
    developer?: { name: string; id?: string }
    mainCategory?: string
    series: CategoryLink[]
    tags: (CategoryLink & { totalCount?: number })[]
}

function toCollectionGame(game: APICollectionGame): CollectionGame {
    const categoriesInfo = game.categoriesInfo || []
    const byType = (type: 'tag' | 'series') => categoriesInfo.filter((c) => c.categoryType === type)

    return {
        hru: game.hru,
        title: game.title,
        icon: game.icon,
        preview: game.big_preview,
        description: game.collection_description,
        rating: { value: game.rating?.value || 0, count: game.rating?.count || 0 },
        platforms: PLATFORM_TYPES.filter((platform) => game.platforms?.includes(platform)),
        updatedAt: game.updatedAt,
        developer: game.developer
            ? { name: game.developer, id: game.developer_id > 0 ? String(game.developer_id) : undefined }
            : undefined,
        mainCategory: categoriesInfo.find((c) => c.id === game.main_category)?.title,
        series: byType('series').map(({ hru, title }) => ({ hru, title })),
        tags: byType('tag').map(({ hru, title, totalCount }) => ({ hru, title, totalCount })),
    }
}

type UseCollectionPageDataParams = {
    hru: Ref<string>
}

export const useCollectionPageData = ({ hru }: UseCollectionPageDataParams) => {
    const appStore = useAppStore()
    const { currentLocale } = useI18nAddon()
    const { getGamesByCategories } = useAPI()

    const {
        categoryInfo: collectionInfo,
        categoryRedirect: redirect,
        fetchStatus: seoFetchStatus,
        hasError: seoHasError,
        suspense: seoSuspense,
    } = useCategorySeoInfo({ categoryName: hru, pageType: computed<CategoryType>(() => 'collection') })

    const gamesQueryKey = computed(() => [
        'collection-games',
        hru.value,
        currentLocale.value,
        appStore.platform,
        appStore.contentScope,
    ])

    const {
        data: gamesData,
        suspense: gamesSuspense,
        isError: gamesHasError,
    } = useQuery({
        queryKey: gamesQueryKey,
        queryFn: async (): Promise<{ games: CollectionGame[]; fetchStatus?: 404 | 410 }> => {
            const response = await getGamesByCategories({
                categoriesIds: [hru.value],
                pagination: {
                    page: 1,
                    pageSize: 20,
                },
                platform: appStore.platform,
                locale: currentLocale.value,
                withDebugInfo: false,
                pageType: 'collection',
                rankingClusterId: undefined,
                rankingAudience: undefined,
                includeUnsupportedPlatforms: !appStore.contentScope,
                scope: appStore.contentScope,
            })

            if (response.status === 'error') {
                throw new Error(response.message)
            }

            if (response.status === 'not-found') {
                return { games: [], fetchStatus: response.httpStatus }
            }

            const games = (response.data.categories[0]?.games || []) as APICollectionGame[]

            return { games: games.map(toCollectionGame) }
        },
        persister: usePersisterFn(Persisters.MediumCardinality),
    })

    const games = computed(() => gamesData.value?.games || [])
    const fetchStatus = computed(() => seoFetchStatus.value || gamesData.value?.fetchStatus)
    const hasError = computed(() => seoHasError.value || gamesHasError.value)

    const preload = async () => {
        await Promise.all([seoSuspense(), gamesSuspense()])
    }

    return {
        collectionInfo,
        games,
        redirect,
        fetchStatus,
        hasError,
        preload,
    }
}
